import { Box, Text } from '@mantine/core';
import { ComponentType, ReactNode } from 'react';
import { Redirect } from 'wouter';
import { Permission, usePermissions } from '@/models/permissions';

interface ProtectedRouteProps {
  /**
   * The component to render if permission check passes
   */
  component: ComponentType<any>;

  /**
   * Required permissions to access this route
   */
  permissions?: Permission[];

  /**
   * If true, user must have ALL permissions.
   * If false/undefined, user needs ANY one permission.
   */
  requireAll?: boolean;

  /**
   * Custom component to render when permission is denied
   */
  fallback?: ReactNode;

  /**
   * Path to redirect to when permission is denied
   */
  redirectTo?: string;

  /**
   * Any props to pass to the component
   */
  [key: string]: any;
}

/**
 * ProtectedRoute component that guards routes based on user permissions
 *
 * Usage:
 * ```tsx
 * <Route
 *   path="/proposals"
 *   component={(props) => (
 *     <ProtectedRoute
 *       component={ProposalsPage}
 *       permissions={[PERMISSIONS.PROPOSALS_READ]}
 *       {...props}
 *     />
 *   )}
 * />
 * ```
 *
 * Or with custom fallback:
 * ```tsx
 * <ProtectedRoute
 *   component={ProposalsPage}
 *   permissions={[PERMISSIONS.PROPOSALS_READ]}
 *   fallback={<div>Access Denied</div>}
 * />
 * ```
 */
export function ProtectedRoute({
  component: Component,
  permissions,
  requireAll = false,
  fallback,
  redirectTo,
  ...props
}: ProtectedRouteProps) {
  const { permissionManager, isLoading } = usePermissions();

  // If no permissions required, render the component directly
  if (!permissions || permissions.length === 0) {
    return <Component {...props} />;
  }

  // Show loading state while permissions are being fetched
  if (isLoading) {
    return (
      <Box
        p="xl"
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <Text>Loading...</Text>
      </Box>
    );
  }

  // Check permissions
  const hasPermission = requireAll
    ? permissionManager.hasAllPermissions(permissions)
    : permissionManager.hasAnyPermission(permissions);

  // If user doesn't have permission
  if (!hasPermission) {
    // Redirect if redirectTo is provided
    if (redirectTo) {
      return <Redirect to={redirectTo} replace />;
    }

    // Use custom fallback if provided
    if (fallback) {
      return <>{fallback}</>;
    }

    // Default fallback
    return (
      <Box p="xl">
        <Text size="lg" fw={500} mb="md">
          Access Denied
        </Text>
        <Text c="dimmed">
          You do not have permission to access this page. Please contact your
          administrator if you believe this is an error.
        </Text>
      </Box>
    );
  }

  // User has permission, render the component
  return <Component {...props} />;
}

/**
 * Higher-order component that wraps a component with permission protection
 *
 * Usage:
 * ```tsx
 * const ProtectedProposalsPage = withPermissions(
 *   ProposalsPage,
 *   [PERMISSIONS.PROPOSALS_READ]
 * );
 *
 * <Route path="/proposals" component={ProtectedProposalsPage} />
 * ```
 */
export function withPermissions<P extends object>(
  Component: ComponentType<P>,
  permissions: Permission[],
  requireAll = false,
  fallback?: ReactNode,
  redirectTo?: string,
): ComponentType<P> {
  return (props: P) => (
    <ProtectedRoute
      component={Component}
      permissions={permissions}
      requireAll={requireAll}
      fallback={fallback}
      redirectTo={redirectTo}
      {...props}
    />
  );
}
